import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  type AdfTable,
  type TexecLinkedWorkItem,
  type YesNo,
} from '../../lib/texecApi';
import { useRunStore } from '../../state/runStore';
import { JiraKeyLink } from '../../components/JiraKeyLink';

function NotSet() {
  return <span className="muted small">not set</span>;
}

function YesNoPill({ value }: { value: YesNo }) {
  if (value === null) return <NotSet />;
  return (
    <span className={`badge ${value === 'yes' ? 'good' : 'grey'} small`}>{value}</span>
  );
}

function TagList({ values }: { values: string[] }) {
  if (values.length === 0) return <NotSet />;
  return (
    <div className="row" style={{ flexWrap: 'wrap', gap: 6 }}>
      {values.map((v) => (
        <span key={v} className="badge small">
          {v}
        </span>
      ))}
    </div>
  );
}

function ScalarValue({ value }: { value: string | null }) {
  if (!value || !value.trim()) return <NotSet />;
  return <span>{value}</span>;
}

function AdfTableView({ table }: { table: AdfTable | null }) {
  if (!table || (table.headers.length === 0 && table.rows.length === 0)) return <NotSet />;
  return (
    <div className="data-table-wrap">
      <table className="data-table">
        {table.headers.length > 0 && (
          <thead>
            <tr>
              {table.headers.map((h, i) => (
                <th key={i}>{h}</th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {table.rows.map((row, ri) => (
            <tr key={ri}>
              {row.map((cell, ci) => (
                <td key={ci} style={{ whiteSpace: 'pre-wrap' }}>
                  {cell || ''}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function statusBadgeClass(status: string | null): string {
  if (!status) return 'grey';
  const s = status.toLowerCase();
  if (s.includes('done') || s.includes('closed') || s.includes('resolved')) return 'good';
  if (s.includes('progress') || s.includes('review') || s.includes('preparation')) return 'warn';
  if (s.includes('to do') || s.includes('open') || s.includes('reopened')) return 'info';
  if (s.includes('block') || s.includes('cancel')) return 'bad';
  return 'grey';
}

function LinkedWorkItemRow({ item }: { item: TexecLinkedWorkItem }) {
  return (
    <div className="linked-item">
      <JiraKeyLink keyId={item.key} className="badge mono small linked-item-key" />
      <span className="linked-item-summary" title={item.summary}>
        {item.summary || '(no summary)'}
      </span>
      {item.linkType && (
        <span className="badge small linked-item-type" title="Link type">
          {item.linkType}
        </span>
      )}
      <span className={`badge ${statusBadgeClass(item.status)} small linked-item-status`}>
        {item.status ?? 'unknown'}
      </span>
    </div>
  );
}

interface DefRowProps {
  label: string;
  children: React.ReactNode;
}

function DefRow({ label, children }: DefRowProps) {
  return (
    <>
      <div className="def-list-label">{label}</div>
      <div className="def-list-value">{children}</div>
    </>
  );
}

/**
 * Phase 1 - read-only TEXEC overview. Landing screen for every run: pulls
 * every field listed in the spec from Jira and renders four labelled cards
 * matching the Jira UI tabs (Header, Product Data, Report Data, Linked work
 * items). No write-back yet. The downstream phases consume the values
 * shown here, which is why this is at the front of the rail.
 *
 * NOTE: this panel still lives under `features/phase0/` for git-history
 * reasons; the visible step number changed to 1 when the rail was
 * reordered. See `App.tsx` for the URL <-> folder mapping.
 */
export function PopulateTexecPanel() {
  const { id } = useParams<{ id: string }>();
  const runId = id!;
  const data = useRunStore((s) => s.texec);
  const loading = useRunStore((s) => s.texecLoading);
  const error = useRunStore((s) => s.texecError);
  const loadTexec = useRunStore((s) => s.loadTexec);

  useEffect(() => {
    if (runId) void loadTexec(runId);
  }, [runId, loadTexec]);

  const load = () => loadTexec(runId, { force: true });

  return (
    <div className="flex-col">
      <div className="card phase-action-card">
        <div className="row">
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ margin: 0 }}>Phase 1 - TEXEC Overview (read-only)</h2>
            <p className="muted small" style={{ margin: '4px 0 0' }}>
              {data ? (
                <>
                  <JiraKeyLink
                    keyId={data.key}
                    className="badge mono small"
                    style={{ marginRight: 6 }}
                  />
                  {data.summary}
                </>
              ) : (
                'Pulled live from Jira; downstream phases consume these values.'
              )}
            </p>
          </div>
          <span className="spacer" />
          <button className="btn small" disabled={loading} onClick={() => void load()}>
            {loading ? 'Refreshing...' : 'Refresh from Jira'}
          </button>
        </div>
        {error && (
          <p style={{ color: 'var(--bad)', marginTop: 8 }}>{error}</p>
        )}
        {data?.meta?.stale && (
          <p
            className="small"
            style={{
              marginTop: 8,
              padding: '8px 10px',
              borderRadius: 6,
              border: '1px solid rgba(210, 153, 34, 0.4)',
              background: 'rgba(210, 153, 34, 0.08)',
              color: 'var(--warn)',
            }}
            title={data.meta.staleReason}
          >
            {data.meta.staleReason ?? 'Showing cached data; live Jira fetch failed.'}
          </p>
        )}
      </div>

      {!data && !loading && !error && (
        <div className="card">
          <p className="muted">No TEXEC data loaded.</p>
        </div>
      )}

      {data && (
        <>
          <div className="card">
            <h3 style={{ marginTop: 0 }}>Header</h3>
            <div className="def-list">
              <DefRow label="Order Type"><ScalarValue value={data.header.orderType} /></DefRow>
              <DefRow label="Tested Jurisdictions"><TagList values={data.header.testedJurisdictions} /></DefRow>
              <DefRow label="Studio"><ScalarValue value={data.header.studio} /></DefRow>
            </div>
          </div>

          <div className="card">
            <h3 style={{ marginTop: 0 }}>Product Data</h3>
            <div className="def-list">
              <DefRow label="Game Name"><ScalarValue value={data.productData.gameName} /></DefRow>
              <DefRow label="Game ID"><ScalarValue value={data.productData.gameId} /></DefRow>
              <DefRow label="Game Version"><ScalarValue value={data.productData.gameVersion} /></DefRow>
              <DefRow label="Game Type"><ScalarValue value={data.productData.gameType} /></DefRow>
              <DefRow label="Game Features"><TagList values={data.productData.gameFeatures} /></DefRow>
              <DefRow label="Client RTP(s)"><AdfTableView table={data.productData.clientRtps} /></DefRow>
              <DefRow label="hasJackpot"><YesNoPill value={data.productData.hasJackpot} /></DefRow>
              <DefRow label="hasBuyFeature"><YesNoPill value={data.productData.hasBuyFeature} /></DefRow>
              <DefRow label="hasBuyChance"><YesNoPill value={data.productData.hasBuyChance} /></DefRow>
              <DefRow label="hasSideBet"><YesNoPill value={data.productData.hasSideBet} /></DefRow>
              <DefRow label="hasGamble"><YesNoPill value={data.productData.hasGamble} /></DefRow>
              <DefRow label="hasBranding"><YesNoPill value={data.productData.hasBranding} /></DefRow>
              <DefRow label="Product Scope"><TagList values={data.productData.productScope} /></DefRow>
            </div>
          </div>

          <div className="card">
            <h3 style={{ marginTop: 0 }}>Report Data</h3>
            <div className="def-list">
              <DefRow label="Jurisdictions"><TagList values={data.reportData.jurisdictions} /></DefRow>
            </div>
          </div>

          <div className="card">
            <div className="row">
              <h3 style={{ margin: 0 }}>Linked work items</h3>
              <span className="muted small" style={{ marginLeft: 8 }}>
                {data.linkedWorkItems.length} {data.linkedWorkItems.length === 1 ? 'item' : 'items'}
              </span>
            </div>
            {data.linkedWorkItems.length === 0 ? (
              <p className="muted small" style={{ marginTop: 8 }}>No linked work items.</p>
            ) : (
              <div className="linked-list" style={{ marginTop: 12 }}>
                {data.linkedWorkItems.map((item) => (
                  <LinkedWorkItemRow key={item.key} item={item} />
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
